import { createContext, useContext, useEffect, useRef, useCallback } from "react";
import { toast } from "@/lib/toast";

/**
 * Lightweight context for upload-adjacent state that needs to outlive any
 * single page (FootageStep → Confirmation).
 *
 * - Active-upload flag: ref-based, signals SessionTimeoutProvider not to log
 *   the user out mid-batch.
 * - Pegasus poll: after each fire-and-forget call to /api/finalize-analysis
 *   (or the local-only chunked-upload path), the client queues a (footageFileId,
 *   displayName, optional sidecar target) entry here. A single background
 *   poller hits the /api/finalize-analysis/status endpoint every
 *   POLL_INTERVAL_MS and:
 *     • on "done"   — writes the .description.txt into the user's local
 *                     folder if one was provided (no-op otherwise).
 *     • on "failed" — fires a per-file warning toast so silent server-side
 *                     Pegasus failures stop being silent.
 *     • on "pending"— decrements the per-entry counter; drops at MAX_POLLS.
 *
 * The poller lives in the provider so it survives route changes — the
 * FootageStep component can unmount and the writes/toasts still land.
 */

const POLL_INTERVAL_MS = 5_000;
/**
 * Polls per entry before we give up — 240 × 5s = 20 minutes. Previously 60
 * (5 min), which silently stranded large Sony A-roll cards in "Analyzing…"
 * limbo because the proxy transcode + Pegasus + embedding chain on a busy
 * server routinely exceeds the 5-minute mark. 20 minutes is generous enough
 * to absorb a worst-case stressed-server run (proxy + Bedrock retries +
 * Whisper) while still bounding the per-entry cost.
 */
const MAX_POLLS = 240; // ~20 minutes per entry

/**
 * Threshold below which we suppress the batch-completion toast. A single
 * clip that finishes in well under 10s never needed a toast — the per-card
 * chip already gave instant feedback, and an extra notification would feel
 * like noise. Above this, the user has likely walked away from the success
 * screen and the toast is the cleanest way to let them know the AI sidecar
 * data is fully saved without making them babysit the page.
 */
const BATCH_TOAST_MIN_DURATION_MS = 10_000;

interface PegasusPollEntry {
  footageFileId: number;
  /** Human-readable filename used in the failure toast. */
  displayName: string;
  /**
   * Local-write target. Both null when the user picked Drive-only — the
   * entry still polls so we can surface a failure toast, just nothing to
   * write locally on success.
   */
  sidecarName: string | null;
  dateFolderHandle: FileSystemDirectoryHandle | null;
  pollsRemaining: number;
  /**
   * Optional UI callback fired exactly once when the poll resolves
   * (either to "done" or "failed"). Lets file cards / success screens
   * surface a per-file "AI saved" indicator without each one needing
   * its own duplicate poll loop. Safe to omit when the caller only
   * cares about the toast + sidecar side-effects.
   */
  onStatusChange?: (status: "done" | "failed") => void;
}

interface UploadContextValue {
  /** Returns true if an upload session is currently in progress. */
  isUploadActive: () => boolean;
  /** Call when the upload batch starts. */
  setUploadActive: (active: boolean) => void;
  /**
   * Queue a footage row whose Pegasus job is in flight server-side. Always
   * polls so we can surface a failure toast; also writes the description
   * sidecar locally when sidecarName + dateFolderHandle are non-null.
   */
  enqueuePegasusPoll: (args: {
    footageFileId: number;
    displayName: string;
    sidecarName: string | null;
    dateFolderHandle: FileSystemDirectoryHandle | null;
    onStatusChange?: (status: "done" | "failed") => void;
  }) => void;
}

const UploadContext = createContext<UploadContextValue>({
  isUploadActive: () => false,
  setUploadActive: () => {},
  enqueuePegasusPoll: () => {},
});

export function UploadProvider({ children }: { children: React.ReactNode }) {
  const activeRef = useRef(false);
  /** Pending poll queue, keyed by footageFileId so duplicate enqueues no-op. */
  const pendingRef = useRef<Map<number, PegasusPollEntry>>(new Map());
  const timerRef = useRef<number | null>(null);
  /**
   * Aggregate counters spanning the current "batch" — every clip the user
   * enqueued since the last drain. When the pending queue empties, these
   * counters drive the summary toast (e.g. "AI analysis complete — 3 of 3
   * clips saved") and persist into the bell-icon history. Reset to zero
   * after each toast fires, so a follow-up batch starts fresh.
   */
  const batchStatsRef = useRef<{
    total: number;
    done: number;
    failed: number;
    timedOut: number;
    startedAt: number;
  }>({ total: 0, done: 0, failed: 0, timedOut: 0, startedAt: 0 });

  const isUploadActive = useCallback(() => activeRef.current, []);
  const setUploadActive = useCallback((active: boolean) => {
    activeRef.current = active;
  }, []);

  const writeDescriptionLocally = useCallback(
    async (
      dateFolderHandle: FileSystemDirectoryHandle,
      filename: string,
      content: string,
    ): Promise<boolean> => {
      try {
        // Mirror the Drive layout: the description sits inside a
        // "sidecars" subfolder next to the video (historically named
        // "Ds+Ts" — see other write sites for the rename). Falls back
        // to the date folder itself if the subfolder can't be created
        // (matches Drive's own fallback in resolveDriveSidecarFolder).
        let target: FileSystemDirectoryHandle = dateFolderHandle;
        try {
          target = await dateFolderHandle.getDirectoryHandle("sidecars", { create: true });
        } catch {
          /* fall through to date-folder-level write */
        }
        const fileHandle = await target.getFileHandle(filename, { create: true });
        const writable = await fileHandle.createWritable();
        await writable.write(content);
        await writable.close();
        return true;
      } catch (err) {
        console.warn(
          `[pegasus-poll] local write failed for ${filename}:`,
          err instanceof Error ? err.message : err,
        );
        return false;
      }
    },
    [],
  );

  /**
   * Fire a single summary toast when the batch queue drains, then reset
   * counters so the next batch starts clean. Suppressed for trivially
   * fast batches (single clip resolving in under 10 s) — the per-card
   * chip is already showing the success state there, so an extra toast
   * is noise. Toast severity reflects the outcome breakdown:
   *   • all done       → success (green)
   *   • some failed    → warning (yellow), with hint about Retry AI
   *   • some timed out → info (blue), nudges user to recheck later
   *   • all failed     → error (red)
   * The toast is auto-recorded into the bell-icon history by toast.ts,
   * so users who walked away during a long Sony A-roll run can come
   * back hours later and still find the confirmation entry.
   */
  const fireBatchToastIfReady = useCallback(() => {
    if (pendingRef.current.size > 0) return;
    const { total, done, failed, timedOut, startedAt } = batchStatsRef.current;
    if (total === 0) return;
    const durationMs = startedAt > 0 ? Date.now() - startedAt : 0;
    // Reset before firing so any toast.X that synchronously re-enqueues
    // (won't today, but defensive) doesn't double-fire on the same batch.
    batchStatsRef.current = { total: 0, done: 0, failed: 0, timedOut: 0, startedAt: 0 };
    const trivial = total === 1 && durationMs < BATCH_TOAST_MIN_DURATION_MS;
    if (trivial) return;

    const pluralClips = total === 1 ? "clip" : "clips";
    if (done === total) {
      toast.success(
        `AI analysis complete — ${done} of ${total} ${pluralClips} saved to the search table.`,
        { duration: 8000 },
      );
    } else if (done === 0 && failed > 0 && timedOut === 0) {
      toast.error(
        `AI analysis failed for all ${failed} ${pluralClips}. Use Retry AI in Footage Search or run a Pegasus backfill from Library Stats.`,
        { duration: 12000 },
      );
    } else if (failed > 0 && timedOut === 0) {
      toast.warning(
        `AI analysis: ${done} of ${total} ${pluralClips} saved, ${failed} failed. Use Retry AI in Footage Search to re-run.`,
        { duration: 12000 },
      );
    } else if (timedOut > 0 && failed === 0) {
      toast.info(
        `AI analysis: ${done} of ${total} ${pluralClips} saved, ${timedOut} still processing. Check Footage Search shortly.`,
        { duration: 10000 },
      );
    } else {
      // Mixed bag — failed + timed out + maybe some done.
      toast.warning(
        `AI analysis finished with issues: ${done} saved, ${failed} failed, ${timedOut} still processing. Check Footage Search.`,
        { duration: 12000 },
      );
    }
  }, []);

  const pollOnce = useCallback(async () => {
    const entries = Array.from(pendingRef.current.values());
    if (entries.length === 0) return;

    await Promise.all(
      entries.map(async (entry) => {
        try {
          const res = await fetch(
            `/api/finalize-analysis/status?footageFileId=${entry.footageFileId}`,
          );
          if (!res.ok) {
            // 401/403/404 — give up immediately, no point retrying. Count
            // as timed-out for the batch summary since we genuinely don't
            // know whether the analysis succeeded server-side.
            if (res.status === 401 || res.status === 403 || res.status === 404) {
              pendingRef.current.delete(entry.footageFileId);
              batchStatsRef.current.timedOut += 1;
            } else {
              entry.pollsRemaining -= 1;
              if (entry.pollsRemaining <= 0) {
                pendingRef.current.delete(entry.footageFileId);
                batchStatsRef.current.timedOut += 1;
              }
            }
            return;
          }
          const body = (await res.json()) as
            | { status: "pending" }
            | { status: "done"; aiDescription: string }
            | { status: "failed" };
          if (body.status === "done") {
            if (entry.dateFolderHandle && entry.sidecarName) {
              await writeDescriptionLocally(
                entry.dateFolderHandle,
                entry.sidecarName,
                body.aiDescription,
              );
            }
            entry.onStatusChange?.("done");
            pendingRef.current.delete(entry.footageFileId);
            batchStatsRef.current.done += 1;
          } else if (body.status === "failed") {
            // Surface the silent server-side failure. Per-file toast; if
            // Pegasus throttles a whole batch the user will see several
            // stacked — that's intentional, they need to know what missed.
            toast.warning(
              `AI description failed for "${entry.displayName}".`,
              { duration: 8000 },
            );
            entry.onStatusChange?.("failed");
            pendingRef.current.delete(entry.footageFileId);
            batchStatsRef.current.failed += 1;
          } else {
            entry.pollsRemaining -= 1;
            if (entry.pollsRemaining <= 0) {
              pendingRef.current.delete(entry.footageFileId);
              batchStatsRef.current.timedOut += 1;
            }
          }
        } catch (err) {
          entry.pollsRemaining -= 1;
          if (entry.pollsRemaining <= 0) {
            pendingRef.current.delete(entry.footageFileId);
            batchStatsRef.current.timedOut += 1;
          }
          console.warn(
            `[pegasus-poll] fetch failed for ${entry.footageFileId}:`,
            err instanceof Error ? err.message : err,
          );
        }
      }),
    );

    // If the batch finished in this tick, send the summary toast. Safe to
    // call every tick — it's a no-op when there's still work in flight or
    // there was no recent activity.
    fireBatchToastIfReady();
  }, [writeDescriptionLocally, fireBatchToastIfReady]);

  const enqueuePegasusPoll = useCallback(
    (args: {
      footageFileId: number;
      displayName: string;
      sidecarName: string | null;
      dateFolderHandle: FileSystemDirectoryHandle | null;
      onStatusChange?: (status: "done" | "failed") => void;
    }) => {
      const { footageFileId, displayName, sidecarName, dateFolderHandle, onStatusChange } = args;
      if (!Number.isFinite(footageFileId) || footageFileId <= 0) return;
      if (pendingRef.current.has(footageFileId)) return;
      pendingRef.current.set(footageFileId, {
        footageFileId,
        displayName,
        sidecarName,
        dateFolderHandle,
        pollsRemaining: MAX_POLLS,
        onStatusChange,
      });
      // Batch bookkeeping for the summary toast. startedAt is stamped on
      // the first enqueue of a fresh batch (i.e. when total goes 0 → 1)
      // and used to compute the duration heuristic that suppresses
      // trivial single-clip toasts.
      if (batchStatsRef.current.total === 0) {
        batchStatsRef.current.startedAt = Date.now();
      }
      batchStatsRef.current.total += 1;
    },
    [],
  );

  // Single shared interval — fires while at least one entry is pending.
  // pollOnce is a cheap no-op when the queue is empty.
  useEffect(() => {
    const tick = () => {
      void pollOnce();
    };
    timerRef.current = window.setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      if (timerRef.current !== null) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [pollOnce]);

  return (
    <UploadContext.Provider value={{ isUploadActive, setUploadActive, enqueuePegasusPoll }}>
      {children}
    </UploadContext.Provider>
  );
}

export function useUploadContext() {
  return useContext(UploadContext);
}
